import { useEffect } from "react";
import * as turf from "@turf/turf";

import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { fitBounds } from "@/utils/map";
import { DEFAULT_PADDING } from "@/components/maps/constants";

/**
 * Hook for managing the radius circle overlay and view mode transitions
 * @param {Object} options
 * @param {boolean} options.isMapReady - Whether the map is ready
 * @param {React.RefObject} options.mapRef - Reference to MarketMapBase
 * @param {Object} options.pickupLocation - Pickup location data from Redux
 * @param {string} options.pickupRadius - Pickup radius value
 * @param {string} options.mapViewMode - Current view mode ("fullMap" | "market")
 * @param {boolean} options.shouldSetPins - Whether pins should be set (triggers overlay reorder)
 */
export const useRadiusCircle = ({ isMapReady, mapRef, pickupLocation, pickupRadius, mapViewMode, shouldSetPins, isLoadboard }) => {
  const isDarkTheme = useIsDarkTheme();

  // Handle map view mode changes (full map vs market zoom)
  useEffect(() => {
    if (!isMapReady || !mapRef.current || !pickupLocation.data) return;

    const map = mapRef.current.getMap();
    const markets = mapRef.current.getMarkets();

    if (!map) return;

    // Remove existing radius circle and overlay
    if (map.getLayer("radius-circle-layer")) {
      map.removeLayer("radius-circle-layer");
    }
    if (map.getLayer("radius-circle-outline")) {
      map.removeLayer("radius-circle-outline");
    }
    if (map.getLayer("radius-outside-overlay")) {
      map.removeLayer("radius-outside-overlay");
    }
    if (map.getSource("radius-circle")) {
      map.removeSource("radius-circle");
    }
    if (map.getSource("radius-outside")) {
      map.removeSource("radius-outside");
    }

    const pickupPoint = [pickupLocation.data.longitude, pickupLocation.data.latitude];
    const hasRadiusSearch = pickupRadius && pickupRadius !== "market-area";

    // In fullMap mode with radius search, remove extended market borders
    if (mapViewMode === "fullMap" && hasRadiusSearch) {
      mapRef.current.removeSelectedMarketLayer();
    }

    if (mapViewMode === "market") {
      if (hasRadiusSearch) {
        // Radius-based search: show circle around pickup point with overlay
        const radiusMiles = parseInt(pickupRadius);
        const circle = turf.circle(pickupPoint, radiusMiles, { steps: 64, units: "miles" });

        const outerBounds = turf.bboxPolygon([-130, 20, -60, 55]);
        const outsideCircle = turf.difference(turf.featureCollection([outerBounds, circle]));

        map.addSource("radius-circle", {
          type: "geojson",
          data: circle,
        });

        if (outsideCircle) {
          map.addSource("radius-outside", {
            type: "geojson",
            data: outsideCircle,
          });

          map.addLayer({
            id: "radius-outside-overlay",
            type: "fill",
            source: "radius-outside",
            paint: {
              "fill-color": isDarkTheme ? "#131414" : "#FFFFFF",
              "fill-opacity": 0.8,
            },
          });
        }

        map.addLayer({
          id: "radius-circle-outline",
          type: "line",
          source: "radius-circle",
          paint: {
            "line-color": isDarkTheme ? "#8EE4CA" : "#2E5D4B",
            "line-width": 2,
            "line-opacity": 0.8,
          },
        });

        const bbox = turf.bbox(circle);
        const marketViewPadding = isLoadboard
          ? { top: 77, bottom: 75, left: 377, right: 115 }
          : { top: 40, bottom: 40, left: 250, right: 40 };
        map.fitBounds(
          [
            [bbox[0], bbox[1]],
            [bbox[2], bbox[3]],
          ],
          { padding: marketViewPadding, duration: 400 },
        );
      } else {
        // Market-area search: zoom to show the market boundary
        const marketName = pickupLocation.data.market?.city;
        if (markets?.data && marketName) {
          const marketFeature = markets.data.features.find((f) => f.properties.marketName === marketName);
          if (marketFeature) {
            const bbox = turf.bbox(marketFeature);
            const marketViewPadding = isLoadboard
              ? { top: 76, bottom: 76, left: 296, right: 196 }
              : { top: 60, bottom: 60, left: 300, right: 60 };
            map.fitBounds(
              [
                [bbox[0], bbox[1]],
                [bbox[2], bbox[3]],
              ],
              { padding: marketViewPadding, duration: 400 },
            );
          }
        }
      }
    } else {
      // Full map view
      if (hasRadiusSearch) {
        const radiusMiles = parseInt(pickupRadius);
        const circle = turf.circle(pickupPoint, radiusMiles, { steps: 64, units: "miles" });

        map.addSource("radius-circle", {
          type: "geojson",
          data: circle,
        });

        map.addLayer({
          id: "radius-circle-outline",
          type: "line",
          source: "radius-circle",
          paint: {
            "line-color": isDarkTheme ? "#8EE4CA" : "#2E5D4B",
            "line-width": 2,
            "line-opacity": 0.8,
          },
        });
      }

      fitBounds(map, DEFAULT_PADDING, 400);
    }
  }, [isMapReady, mapViewMode, pickupLocation.data, pickupRadius, isDarkTheme]);

  // Ensure overlay stays on top when filters change
  useEffect(() => {
    if (!isMapReady || !mapRef.current || mapViewMode !== "market") return;

    const map = mapRef.current.getMap();
    if (!map) return;

    const hasRadiusSearch = pickupRadius && pickupRadius !== "market-area";

    if (hasRadiusSearch && pickupLocation.data) {
      if (map.getLayer("radius-outside-overlay")) {
        map.moveLayer("radius-outside-overlay");
      }
      if (map.getLayer("radius-circle-outline")) {
        map.moveLayer("radius-circle-outline");
      }
    }
  }, [isMapReady, shouldSetPins, mapViewMode, pickupRadius, pickupLocation.data]);
};
